<template>
  <div class="timeline-wrapper">
    <div class="timeline-header">
      <h3 class="timeline-title">
        <a-icon type="history" class="title-icon" />
        时间线
      </h3>
      <div class="timeline-actions">
        <a-select
          v-model="selectedTableNames"
          mode="multiple"
          placeholder="筛选记录类型"
          style="min-width: 260px; margin-right: 12px"
          allowClear
          @change="handleTableFilterChange">
          <a-select-option value="breed_items">个体信息</a-select-option>
          <a-select-option value="item_select_records">选配记录</a-select-option>
          <a-select-option value="biao_xing_records">表型记录</a-select-option>
          <a-select-option value="production_records">生产记录</a-select-option>
          <a-select-option value="event_records">事件记录</a-select-option>
          <a-select-option value="yu_zhong_records">育种记录</a-select-option>
          <a-select-option value="farm_cell_items">栏舍记录</a-select-option>
        </a-select>
        <a-button size="small" icon="reload" @click="refresh">刷新</a-button>
      </div>
    </div>
    
    <div class="timeline-container">
      <a-list
        :loading="timelineLoading"
        :data-source="timelineData"
        class="timeline-list">
        <div
          v-if="hasMoreTimeline"
          slot="loadMore"
          :style="{ textAlign: 'center', marginTop: '12px', height: '32px', lineHeight: '32px' }">
          <a-spin v-if="loadingMore" />
          <a-button v-else @click="loadMoreTimeline">加载更多</a-button>
        </div>
        <a-list-item slot="renderItem" slot-scope="item" class="timeline-item">
          <div 
            class="timeline-card-wrapper"
            :class="[`accent-${item.type}`, { 'expanded': item.expanded }]">
            <a-card 
              :bordered="false" 
              class="timeline-card"
              :bodyStyle="{ padding: '16px 24px' }"
              @click="toggleExpand(item)">
              <a-row :gutter="20">
                <a-col :span="4">
                  <div class="timeline-date">
                    <div class="date-wrapper">
                      <a-icon type="clock-circle" class="date-icon" />
                      <div class="date-text">{{ item.date }}</div>
                      <div class="time-text">{{ item.time }}</div>
                    </div>
                  </div>
                </a-col>
                <a-col :span="20">
                  <div class="timeline-content">
                    <div class="content-main">
                      <a-tag :color="getTypeColor(item.type)" class="type-tag">
                        {{ getTypeName(item.type) }}
                      </a-tag>
                      <div class="snapshot-title" v-if="item.title">{{ item.title }}</div>
                      <div class="snapshot-description">{{ item.content }}</div>
                    </div>
                    <div v-if="item.operator" class="operator-text">
                      操作人：{{ item.operator }}
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
            
            <!-- 可展开详情区域 -->
            <div v-if="item.expanded" class="timeline-expanded">
              <div v-if="item.details" class="snapshot-details">
                <a-descriptions :column="{ xs: 1, sm: 2, md: 3 }" size="small" bordered>
                  <a-descriptions-item
                    v-for="(value, key) in item.details"
                    :key="key"
                    :label="key">
                    {{ value }}
                  </a-descriptions-item>
                </a-descriptions>
              </div>
              <!-- 选配记录跳转按钮 -->
              <div v-if="item.type === 'select' && item.tableName === 'item_select_records'" class="action-bar">
                <a-button type="primary" size="small" icon="link" @click.stop="goToSelectRecord(item)">
                  查看选配记录
                </a-button>
              </div>
            </div>
          </div>
        </a-list-item>
      </a-list>
      <div v-if="!hasMoreTimeline && timelineData.length > 0" class="no-more-data">
        没有更多数据了
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ItemTimeline',
  props: {
    itemId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      timelineData: [],
      timelineLoading: false,
      loadingMore: false,
      hasMoreTimeline: true,
      selectedTableNames: [],
      timelinePage: {
        page_no: 1,
        page_size: 10,
        total: 0
      }
    }
  },
  watch: {
    itemId: {
      handler(newVal, oldVal) {
        // 只有当 itemId 真正发生变化时才加载
        if (newVal && newVal !== oldVal) {
          this.loadTimelineData(true)
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载时间线数据
    async loadTimelineData(reset = false) {
      // 防止重复请求
      if (this.timelineLoading || this.loadingMore) {
        return
      }
      
      if (reset) {
        this.timelinePage.page_no = 1
        this.timelineData = []
        this.hasMoreTimeline = true
      }
      
      this.timelineLoading = reset
      this.loadingMore = !reset
      
      try {
        const params = {
          breed_item_id: this.itemId,
          page_no: this.timelinePage.page_no,
          page_size: this.timelinePage.page_size
        }
        
        // 如果有筛选表名，添加到参数中
        if (this.selectedTableNames && this.selectedTableNames.length > 0) {
          params['table_names[]'] = this.selectedTableNames
        }
        
        const res = await this.$api.get_timeline(params)
        
        if (res.code === 200) {
          const formattedData = this.formatTimelineData(res.data || [])
          this.timelineData.push(...formattedData)
          
          // 从返回的 page 对象获取分页信息
          if (res.page) {
            this.timelinePage.total = res.page.total || 0
            this.hasMoreTimeline = this.timelineData.length < this.timelinePage.total
          } else {
            // 如果没有 page 对象，根据数据长度判断
            this.hasMoreTimeline = formattedData.length === this.timelinePage.page_size
          }
        } else {
          this.$message.error(res.message || '加载历史记录失败')
        }
      } catch (error) {
        console.error('加载时间线数据失败:', error)
        this.$message.error('加载历史记录失败')
      } finally {
        this.timelineLoading = false
        this.loadingMore = false
      }
    },
    
    // 加载更多
    loadMoreTimeline() {
      this.timelinePage.page_no++
      this.loadTimelineData(false)
    },
    
    // 刷新时间线
    refresh() {
      this.loadTimelineData(true)
    },
    
    // 处理表类型筛选变化
    handleTableFilterChange() {
      this.loadTimelineData(true)
    },
    
    // 处理卡片点击展开/收起
    toggleExpand(item) {
      // 使用 Vue.set 确保响应式更新
      this.$set(item, 'expanded', !item.expanded)
    },
    
    // 跳转到选配记录页面
    goToSelectRecord(item) {
      if (item.rawData && item.rawData.farm_id) {
        this.$router.push({
          path: '/farms/cell/' + item.rawData.farm_id,
          query: {
            group_type: 'selectRecord'
          }
        })
      }
    },
    
    // 获取类型颜色
    getTypeColor(type) {
      const colorMap = {
        'create': 'blue',
        'update': 'green',
        'event': 'orange',
        'record': 'purple',
        'select': 'cyan',
        'move': 'geekblue'
      }
      return colorMap[type] || 'default'
    },
    
    // 获取类型名称
    getTypeName(type) {
      const nameMap = {
        'create': '创建',
        'update': '更新',
        'event': '事件',
        'record': '记录',
        'select': '选配',
        'move': '移动'
      }
      return nameMap[type] || '未知'
    },
    
    // 格式化时间线数据
    formatTimelineData(data) {
      return data.map(item => {
        const date = new Date(item.created_at)
        return {
          id: item.data.id,
          date: date.toLocaleDateString('zh-CN'),
          time: date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
          type: this.getTypeByTableName(item.table_name),
          title: this.getTitleByTableName(item.table_name, item.data),
          content: this.getContentByTableName(item.table_name, item.data),
          operator: item.operator_name || null,
          details: this.getDetailsByTableName(item.table_name, item.data),
          rawData: item.data,
          tableName: item.table_name
        }
      })
    },
    
    // 根据表名获取类型
    getTypeByTableName(tableName) {
      const typeMap = {
        'breed_items': 'create',
        'item_select_records': 'select',
        'biao_xing_records': 'record',
        'production_records': 'record',
        'event_records': 'event',
        'yu_zhong_records': 'record',
        'farm_cell_items': 'move'
      }
      return typeMap[tableName] || 'update'
    },
    
    // 根据表名获取标题
    getTitleByTableName(tableName, data) {
      const titleMap = {
        'breed_items': `创建个体 ${data.vid || ''}`,
        'item_select_records': `选配记录 ${data.name || ''}`,
        'biao_xing_records': '表型记录',
        'production_records': '生产记录',
        'event_records': `事件记录 ${data.event_type_name || ''}`,
        'yu_zhong_records': '育种记录',
        'farm_cell_items': '个体移动'
      }
      return titleMap[tableName] || '数据更新'
    },
    
    // 根据表名获取内容描述
    formatDateToYMD(dateInput) {
      if (!dateInput) return '';
      const s = typeof dateInput === 'string' ? dateInput : String(dateInput);
      // Already YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      // YYYY-MM-DD HH:mm:ss or YYYY-MM-DD HH:mm:ss.ffffff
      const m = s.match(/^(\d{4}-\d{2}-\d{2})[\s T]/);
      if (m) return m[1];
      // Fallback: parse as Date
      const d = new Date(dateInput);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return s;
    },
    
    // 根据表名获取内容描述
    getContentByTableName(tableName, data) {
      switch (tableName) {
        case 'breed_items':
          return `创建了个体 ${data.vid || ''}，出生日期：${data.birth_date || '未知'}，性别：${data.item_gender === 1 ? '公' : '母'}`
        case 'item_select_records':
          return `创建了选配记录 "${data.name || ''}"`
        case 'biao_xing_records':
          return '添加了表型记录数据'
        case 'production_records':
          return `创建了生产记录，产量：${data.product_weight || 0}kg`
        case 'event_records':
          return `发生了${data.event_type_name || '事件'}：${data.description || ''}`
        case 'yu_zhong_records':
          return '创建了育种记录'
        case 'farm_cell_items':
          return '个体位置发生变更'
        default:
          return '数据发生了变更'
      }
    },
    
    // 根据表名获取详情
    getDetailsByTableName(tableName, data) {
      const details = {}
      
      switch (tableName) {
        case 'breed_items':
          if (data.vid) details['个体编号'] = data.vid
          if (data.rf_id) details['RF-ID'] = data.rf_id
          if (data.birth_date) details['出生日期'] = data.birth_date
          if (data.item_gender !== undefined) details['性别'] = data.item_gender === 1 ? '公' : '母'
          break
        case 'item_select_records':
          if (data.name) details['记录名称'] = data.name
          if (data.farm_id) details['农场ID'] = data.farm_id
          break
        case 'biao_xing_records':
          if (data.recorded_at) details['记录日期'] = this.formatDateToYMD(data.recorded_at)
          if (data.height) details['身高(cm)'] = data.height
          if (data.shen_chang) details['身长(cm)'] = data.shen_chang
          if (data.weight) details['体重(kg)'] = data.weight
          if (data.width) details['体宽(cm)'] = data.width
          if (data.chest_girth) details['胸围(cm)'] = data.chest_girth
          break
        case 'yu_zhong_records':
          if (data.id) details['育种记录ID'] = data.id
          if (data.male_vid) details['雄性个体编号'] = data.male_vid
          if (data.female_vid) details['雌性个体编号'] = data.female_vid
          if (data.item_select_record_name) details['选配记录名称'] = data.item_select_record_name
          break
        case 'production_records':
          if (data.id) details['产量记录ID'] = data.id
          if (data.farm_id) details['农场ID'] = data.farm_id
          if (data.item_id) details['个体ID'] = data.item_id
          if (data.product_weight) details['产量(千克)'] = data.product_weight
          if (data.fat_rate) details['乳脂率(%)'] = data.fat_rate
          if (data.dry_mass) details['干物质(千克)'] = data.dry_mass
          if (data.recorded_at) details['记录日期'] = this.formatDateToYMD(data.recorded_at)
          if (data.record_type !== undefined) {
            const recordTypeText = data.record_type === 1 ? '实际记录' : data.record_type === 2 ? '估算记录' : '未知类型'
            details['记录类型'] = `${data.record_type} (${recordTypeText})`
          }
          break
        case 'event_records':
          if (data.id) details['事件记录ID'] = data.id
          if (data.event_type_name) details['事件类型'] = data.event_type_name
          if (data.description) details['事件描述'] = data.description
          if (data.user_id) details['操作人ID'] = data.user_id
          if (data.recordable_type) details['关联类型'] = data.recordable_type
          if (data.recordable_id) details['关联ID'] = data.recordable_id
          break
        default:
          // 显示所有非ID字段
          Object.keys(data).forEach(key => {
            if (key !== 'id' && key !== 'farm_id' && data[key]) {
              details[key] = String(data[key])
            }
          })
      }
      
      return Object.keys(details).length > 0 ? details : null
    },
  }
}
</script>

<style scoped>
/* 时间线外层容器 - 更简洁 */
.timeline-wrapper {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

/* 时间线头部 */
.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0;
}

.timeline-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 20px;
  color: #8c8c8c;
}

.timeline-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.timeline-actions .ant-select {
  cursor: pointer;
}

.timeline-actions .ant-select .ant-select-selection {
  cursor: pointer;
}

/* 时间线容器 */
.timeline-container {
  margin-top: 0;
}

.timeline-list {
  padding: 0;
}

/* 时间线列表项 - 更紧凑 */
.timeline-item {
  border: none;
  padding: 0 0 12px 0;
}

/* 时间线卡片包装器 - 带左侧主题色高亮边框 */
.timeline-card-wrapper {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.25s ease;
  background: #fff;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.timeline-card-wrapper::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  z-index: 1;
  transition: width 0.2s ease;
}

/* 不同类型的高亮边框颜色 */
.timeline-card-wrapper.accent-create::before {
  background: linear-gradient(180deg, #1677ff 0%, #69b1ff 100%);
}

.timeline-card-wrapper.accent-update::before {
  background: linear-gradient(180deg, #52c41a 0%, #95de64 100%);
}

.timeline-card-wrapper.accent-event::before {
  background: linear-gradient(180deg, #fa8c16 0%, #ffc53d 100%);
}

.timeline-card-wrapper.accent-record::before {
  background: linear-gradient(180deg, #722ed1 0%, #b37feb 100%);
}

.timeline-card-wrapper.accent-select::before {
  background: linear-gradient(180deg, #13c2c2 0%, #5cdbd3 100%);
}

.timeline-card-wrapper.accent-move::before {
  background: linear-gradient(180deg, #2f54eb 0%, #85a5ff 100%);
}

/* 展开状态下的高亮边框 */
.timeline-card-wrapper.expanded::before {
  width: 6px;
}

/* 时间线卡片 */
.timeline-card {
  background: transparent;
  border-radius: 0;
  transition: all 0.25s ease;
  border: none;
  box-shadow: none;
  cursor: pointer;
  margin-left: 4px;
}

.timeline-card:hover {
  background: rgba(0, 0, 0, 0.01);
}

.timeline-card-wrapper:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border-color: #e0e0e0;
}

/* 展开状态下的卡片 */
.timeline-card-wrapper.expanded {
  border-radius: 12px;
}

.timeline-card-wrapper.expanded .timeline-card {
  border-bottom: 1px dashed #e8e8e8;
}

/* 展开详情区域 */
.timeline-expanded {
  background: #fafafa;
  padding: 16px 24px;
  border-radius: 0 0 11px 0;
  animation: slideDown 0.25s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 操作栏 */
.action-bar {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  justify-content: flex-end;
}

/* 时间显示 - 更简洁的样式 */
.timeline-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: 4px;
  background: transparent;
  height: 100%;
}

.date-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: transparent;
  border-radius: 10px;
  min-width: 72px;
}

.date-icon {
  font-size: 18px;
  color: #bfbfbf;
  margin-bottom: 8px;
}

.date-text {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;
  line-height: 1.4;
  white-space: nowrap;
}

.time-text {
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.3;
}

/* 内容区域 */
.timeline-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding-left: 8px;
}

.content-main {
  flex: 1;
}

.content-header {
  margin-bottom: 10px;
}

/* 标签样式 - 更柔和的颜色 */
.type-tag {
  font-size: 12px;
  padding: 3px 12px;
  border-radius: 20px;
  font-weight: 500;
  border: none;
}

/* 自定义标签颜色 - 更柔和 */
.type-tag.ant-tag-blue {
  background: #e6f4ff;
  color: #1677ff;
}

.type-tag.ant-tag-green {
  background: #f6ffed;
  color: #52c41a;
}

.type-tag.ant-tag-orange {
  background: #fff7e6;
  color: #fa8c16;
}

.type-tag.ant-tag-purple {
  background: #f9f0ff;
  color: #722ed1;
}

.type-tag.ant-tag-cyan {
  background: #e6fffb;
  color: #13c2c2;
}

.type-tag.ant-tag-geekblue {
  background: #f0f5ff;
  color: #2f54eb;
}

.operator-text {
  font-size: 11px;
  color: #bfbfbf;
  text-align: right;
  margin-top: 4px;
}

.snapshot-content {
  padding: 4px 0;
}

.snapshot-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin: 8px 0 6px 0;
  line-height: 1.5;
}

.snapshot-description {
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.6;
  margin-bottom: 0;
}

.snapshot-details {
  margin-top: 8px;
}

/* 详情表格样式优化 */
.snapshot-details ::v-deep .ant-descriptions-bordered {
  border-radius: 8px;
  overflow: hidden;
}

.snapshot-details ::v-deep .ant-descriptions-bordered .ant-descriptions-item-label {
  background: #fafafa;
  font-size: 12px;
  color: #8c8c8c;
  padding: 8px 12px;
}

.snapshot-details ::v-deep .ant-descriptions-bordered .ant-descriptions-item-content {
  font-size: 12px;
  color: #595959;
  padding: 8px 12px;
}

/* 没有更多数据 */
.no-more-data {
  text-align: center;
  padding: 20px;
  color: #bfbfbf;
  font-size: 13px;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .timeline-wrapper {
    margin-top: 24px;
    padding-top: 16px;
  }
  
  .timeline-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .timeline-title {
    font-size: 16px;
  }
  
  .title-icon {
    font-size: 18px;
  }
  
  .timeline-actions {
    width: 100%;
    flex-direction: column;
    gap: 8px;
  }
  
  .timeline-actions .ant-select {
    width: 100% !important;
    min-width: auto !important;
    margin-right: 0 !important;
  }
  
  .timeline-actions .ant-btn {
    width: 100%;
  }
  
  .timeline-card ::v-deep .ant-row {
    display: flex;
    flex-direction: column;
  }
  
  .timeline-card ::v-deep .ant-col-4,
  .timeline-card ::v-deep .ant-col-20 {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
  }
  
  .timeline-date {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 0;
    padding: 0 0 8px 0;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 8px;
    height: auto;
  }
  
  .date-wrapper {
    flex-direction: row;
    gap: 8px;
    padding: 6px 10px;
    min-width: auto;
    background: transparent;
  }
  
  .date-icon {
    margin-bottom: 0;
    font-size: 14px;
  }
  
  .date-text {
    font-size: 12px;
  }
  
  .time-text {
    font-size: 10px;
  }
  
  .timeline-content {
    padding-left: 0;
  }
  
  .operator-text {
    text-align: left;
    margin-top: 8px;
  }
  
  .timeline-expanded {
    padding: 12px;
  }
  
  .snapshot-title {
    font-size: 13px;
  }
  
  .snapshot-description {
    font-size: 12px;
  }
  
  .action-bar {
    justify-content: center;
  }
  
  .action-bar .ant-btn {
    width: 100%;
  }
}


</style>
